/**
 * P0 Privacy Policy Page
 */
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Privacy Policy</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: January 2025
        </Typography>

        <Box sx={{ '& > section': { mb: 4 } }}>
          <section>
            <Typography variant="h6" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              MindWell ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our practice management platform.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information that you provide directly to us, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Account Information:</strong> Name, email, phone number, role (doctor, receptionist, patient)</li>
              <li><strong>Professional Information:</strong> Qualifications, specializations, registration numbers, clinic details</li>
              <li><strong>Patient Data:</strong> Medical records, appointment history, prescriptions, clinical notes</li>
              <li><strong>Usage Data:</strong> How you interact with the Service, features used, timestamps</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Provide, maintain, and improve the Service</li>
              <li>Process appointments and manage patient records</li>
              <li>Send notifications and reminders</li>
              <li>Respond to support requests</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              4. Data Storage and Location
            </Typography>
            <Typography variant="body1" paragraph>
              Your data is stored on secure servers. We use industry-standard encryption and security measures to protect your information. Data is stored in compliance with applicable data protection regulations.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              5. Data Backup and Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We maintain automated daily backups of your data with a retention period of 7-30 days. You can request data deletion or export at any time by contacting support@mindwell.com.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              6. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell your personal information. We may share your information only in the following circumstances:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service Providers:</strong> With trusted third-party services that help us operate (e.g., email providers, hosting services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              7. Data Access and Control
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt-out of non-essential communications</li>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              To exercise these rights, contact us at support@mindwell.com.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              8. Security Measures
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect your data, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure backup procedures</li>
            </Box>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              9. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar technologies to improve your experience, analyze usage, and provide personalized features. You can control cookies through your browser settings.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              10. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              11. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through the Service. Your continued use after changes constitutes acceptance.
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              12. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy, please contact us at:
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> support@mindwell.com
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Support:</strong> Available through the in-app support system
            </Typography>
          </section>

          <section>
            <Typography variant="h6" gutterBottom>
              13. Compliance
            </Typography>
            <Typography variant="body1" paragraph>
              We are committed to complying with applicable data protection laws, including but not limited to relevant healthcare data protection regulations in your jurisdiction.
            </Typography>
          </section>
        </Box>
      </Paper>
    </Container>
  );
};

export default Privacy;

